import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Skeleton,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  ROUTE_COMBINED_UI_NAVIGATION_KEY_SET,
  TranslatableItem,
  TranslatableItemType,
} from '@common/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAutoTranslateFeatureEnabled } from 'config/featureFlags';
import { NotificationStatus } from 'enums';

import { AiSparklesIcon } from 'components/custom-icons';
import { AutoTranslateBodyShim } from 'components/form-builder/language-overrides/AutoTranslateBodyShim';
import { ConfirmationModal } from 'components/modal';
import notification from 'components/notification';
import {
  autoTranslateTranslationsWithApplication,
  deleteSystemTranslations,
  deleteTranslations,
  exportTranslationsWithApplicationToCSV,
  getApplicationLocales,
  getTranslatableItemsWithApplication,
  getTranslationsWithApplication,
  importTranslationsWithApplicationFromCSV,
  upsertTranslationsWithApplication,
} from 'services';

import { TranslatableField } from '../form-builder/language-overrides/TranslatableField';

interface RouteLanguageOverridesModalProps {
  isOpen: boolean;
  onClose: () => void;
  routeId: string;
  versionId: string;
}

interface GroupedItem {
  item: TranslatableItem;
  options: TranslatableItem[];
}

function groupRouteFieldItems(items: TranslatableItem[]): GroupedItem[] {
  const optionsByParent = items
    .filter((item) => item.type === TranslatableItemType.Option)
    .reduce(
      (acc, item) => {
        const fieldMatch = item.key.match(/^(form\.field\.[^.]+)\.options\.[^.]+\.label$/);
        if (fieldMatch) {
          const parentKey = `${fieldMatch[1]}.label`;
          if (!acc[parentKey]) {
            acc[parentKey] = [];
          }
          acc[parentKey].push(item);
        }
        return acc;
      },
      {} as Record<string, TranslatableItem[]>,
    );

  const nonOptions = items.filter((item) => item.type !== TranslatableItemType.Option);
  return nonOptions.map((item) => ({
    item,
    options: optionsByParent[item.key] || [],
  }));
}

export function RouteLanguageOverridesModal({
  isOpen,
  onClose,
  routeId,
  versionId,
}: RouteLanguageOverridesModalProps) {
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [overrides, setOverrides] = useState<Record<string, Record<string, string>>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: onDeleteConfirmOpen,
    onClose: onDeleteConfirmClose,
  } = useDisclosure();

  const { data: localesResponse, isLoading: isLoadingLocales } = useQuery({
    queryKey: ['applicationLocales', routeId],
    queryFn: () => getApplicationLocales(routeId),
    enabled: isOpen && !!routeId,
  });

  const { data: combinedItemsResponse, isLoading: isLoadingItems } = useQuery({
    queryKey: ['translatableItemsWithApplication', versionId, routeId],
    queryFn: () => getTranslatableItemsWithApplication(versionId, routeId),
    enabled: isOpen && !!versionId && !!routeId,
  });

  const {
    data: translationsResponse,
    isLoading: isLoadingTranslations,
    isFetching: isFetchingTranslations,
  } = useQuery({
    queryKey: ['translationsWithApplication', versionId, routeId, selectedLanguage],
    queryFn: () => getTranslationsWithApplication(versionId, routeId, selectedLanguage),
    enabled: isOpen && !!versionId && !!routeId && !!selectedLanguage,
    retry: false,
    refetchOnMount: 'always',
  });

  const locales = useMemo(() => localesResponse?.data || [], [localesResponse?.data]);
  const isSelectedTargetDefaultLocale = useMemo(
    () => locales.find((l) => l.code === selectedLanguage)?.isDefault === true,
    [locales, selectedLanguage],
  );
  const translatableItems = combinedItemsResponse?.data?.form;

  const allRouteItems = useMemo(() => {
    if (!translatableItems?.pages) return [];

    return translatableItems.pages.flatMap((page) =>
      (page.sections || []).flatMap((section) =>
        (section.fields || []).flatMap((field) => field.items),
      ),
    );
  }, [translatableItems]);

  const groupedItems = useMemo(() => groupRouteFieldItems(allRouteItems), [allRouteItems]);

  const routeSystemItems = useMemo(() => {
    const navigationItems = combinedItemsResponse?.data?.system?.navigation || [];
    return navigationItems
      .filter((item) => ROUTE_COMBINED_UI_NAVIGATION_KEY_SET.has(item.key))
      .map(
        (item) =>
          ({
            key: item.key,
            type: TranslatableItemType.Field,
            defaultValue: item.defaultValue,
            label: item.label,
            contextPath: [],
          }) satisfies TranslatableItem,
      );
  }, [combinedItemsResponse?.data?.system?.navigation]);

  useEffect(() => {
    if (translationsResponse?.data && selectedLanguage) {
      setOverrides((prev) => ({
        ...prev,
        [selectedLanguage]: {
          ...(prev[selectedLanguage] || {}),
          ...(translationsResponse.data.translations || {}),
        },
      }));
    }
  }, [translationsResponse, selectedLanguage]);

  useEffect(() => {
    if (!isOpen) {
      setOverrides({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (locales.length > 0 && !selectedLanguage) {
      const defaultLocale = locales.find((l) => l.isDefault);
      setSelectedLanguage(defaultLocale?.code || locales[0]?.code || '');
    }
  }, [locales, selectedLanguage]);

  const getLocaleName = useCallback(
    (localeCode: string) => {
      const locale = locales.find((l) => l.code === localeCode);
      return locale?.description || localeCode;
    },
    [locales],
  );

  const saveMutation = useMutation({
    mutationFn: async ({
      localeCode,
      translations,
    }: {
      localeCode: string;
      translations: Record<string, string>;
    }) => {
      const payload = {
        localeName: getLocaleName(localeCode),
        translations,
        isDefault: false,
        isEnabled: true,
      };

      return upsertTranslationsWithApplication(versionId, routeId, localeCode, payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['translationsWithApplication', versionId, routeId, variables.localeCode],
      });
    },
  });

  const autoTranslateMutation = useMutation({
    mutationFn: async () =>
      autoTranslateTranslationsWithApplication(versionId, routeId, {
        targetLanguage: selectedLanguage,
        sourceLanguage: 'en-US',
        localeName: getLocaleName(selectedLanguage),
      }),
    onSuccess: ({ data }) => {
      setOverrides((prev) => ({
        ...prev,
        [selectedLanguage]: {
          ...(prev[selectedLanguage] || {}),
          ...(data.translations || {}),
        },
      }));
      queryClient.invalidateQueries({
        queryKey: ['translationsWithApplication', versionId, routeId, selectedLanguage],
      });
      queryClient.invalidateQueries({
        queryKey: ['translatableItemsWithApplication', versionId, routeId],
      });
    },
  });

  const isNotFoundError = (e: unknown): boolean => {
    const msg = e instanceof Error ? e.message : String(e);
    return /not found|404/i.test(msg);
  };

  const deleteMutation = useMutation({
    mutationFn: async (localeCode: string) => {
      const errors: Error[] = [];
      try {
        await deleteTranslations(versionId, localeCode);
      } catch (e: unknown) {
        if (!isNotFoundError(e)) {
          errors.push(e instanceof Error ? e : new Error(String(e)));
        }
      }
      try {
        await deleteSystemTranslations(routeId, localeCode);
      } catch (e: unknown) {
        if (!isNotFoundError(e)) {
          errors.push(e instanceof Error ? e : new Error(String(e)));
        }
      }
      if (errors.length > 0) throw errors[0];
    },
    onSuccess: (_, localeCode) => {
      queryClient.invalidateQueries({
        queryKey: ['translationsWithApplication', versionId, routeId, localeCode],
      });
      queryClient.invalidateQueries({ queryKey: ['applicationLocales', routeId] });
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[localeCode];
        return next;
      });
      setSelectedLanguage('');
    },
  });

  const handleOverrideChange = (key: string, value: string) => {
    setOverrides((prev) => ({
      ...prev,
      [selectedLanguage]: {
        ...prev[selectedLanguage],
        [key]: value,
      },
    }));
  };

  const getOverrideValue = (key: string): string => {
    return overrides[selectedLanguage]?.[key] || '';
  };

  const handleSave = async () => {
    const languagesWithOverrides = Object.entries(overrides).filter(([, translations]) =>
      Object.values(translations).some((value) => value.trim() !== ''),
    );

    if (languagesWithOverrides.length === 0) {
      notification(
        'No translations to save',
        'Please enter at least one translation override.',
        NotificationStatus.INFO,
      );
      return;
    }

    try {
      await Promise.all(
        languagesWithOverrides.map(([localeCode, translations]) => {
          const nonEmptyTranslations = Object.fromEntries(
            Object.entries(translations).filter(([, value]) => value.trim() !== ''),
          );
          return saveMutation.mutateAsync({
            localeCode,
            translations: nonEmptyTranslations,
          });
        }),
      );

      notification(
        'Translations saved',
        `Successfully saved translations for ${languagesWithOverrides.length} language(s).`,
        NotificationStatus.SUCCESS,
      );

      onClose();
    } catch (error) {
      notification(
        'Error saving translations',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
        NotificationStatus.ERROR,
        5000,
      );
    }
  };

  const handleDeleteClick = () => {
    if (selectedLanguage) onDeleteConfirmOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLanguage) return;
    try {
      await deleteMutation.mutateAsync(selectedLanguage);
      onDeleteConfirmClose();
      notification(
        'Language deleted',
        `Removed ${getLocaleName(selectedLanguage)} translations.`,
        NotificationStatus.SUCCESS,
      );
    } catch (error) {
      notification(
        'Error deleting language',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
        NotificationStatus.ERROR,
        5000,
      );
    }
  };

  const handleExportTranslations = async () => {
    if (!selectedLanguage || !versionId) {
      notification('Cannot export', 'Please select a language.', NotificationStatus.WARNING);
      return;
    }

    try {
      await exportTranslationsWithApplicationToCSV(
        versionId,
        routeId,
        selectedLanguage,
        'application-route',
      );
      notification(
        'Export successful',
        'Route translations exported to CSV.',
        NotificationStatus.SUCCESS,
      );
    } catch (error) {
      notification(
        'Export failed',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
        NotificationStatus.ERROR,
        5000,
      );
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedLanguage) {
      notification('Cannot import', 'Please select a language.', NotificationStatus.WARNING);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      const result = await importTranslationsWithApplicationFromCSV(
        versionId,
        routeId,
        selectedLanguage,
        file,
      );

      setOverrides((prev) => ({
        ...prev,
        [selectedLanguage]: {
          ...(prev[selectedLanguage] || {}),
          ...(result.translations || {}),
        },
      }));

      queryClient.invalidateQueries({
        queryKey: ['translationsWithApplication', versionId, routeId, selectedLanguage],
      });

      const importedCount =
        (result as { importedCount?: number }).importedCount ??
        Object.keys(result.translations || {}).length;
      const totalRows = (result as { totalRowsInFile?: number }).totalRowsInFile;
      const description =
        totalRows != null && importedCount < totalRows
          ? `Successfully imported ${importedCount} of ${totalRows} translation(s) (${totalRows - importedCount} rows skipped).`
          : `Successfully imported ${importedCount} translation(s).`;
      notification('Import successful', description, NotificationStatus.SUCCESS);
    } catch (error) {
      notification(
        'Import failed',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
        NotificationStatus.ERROR,
        5000,
      );
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAutoTranslate = async () => {
    if (!selectedLanguage || !versionId) return;

    try {
      const result = await autoTranslateMutation.mutateAsync();
      const translatedCount = Object.keys(result.data?.translations || {}).length;
      notification(
        'Auto translation complete',
        `Translated ${translatedCount} item(s) to ${getLocaleName(selectedLanguage)}.`,
        NotificationStatus.SUCCESS,
        4000,
      );
    } catch (error) {
      notification(
        'Auto translation failed',
        error instanceof Error ? error.message : 'An unexpected error occurred.',
        NotificationStatus.ERROR,
        5000,
      );
    }
  };

  const isSaving = saveMutation.isPending;
  const isDeleting = deleteMutation.isPending;
  const isTranslating = autoTranslateMutation.isPending;
  const isBusy = isSaving || isDeleting || isTranslating;

  const isLoading =
    isLoadingLocales || isLoadingItems || (isLoadingTranslations && !overrides[selectedLanguage]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        scrollBehavior="inside"
        closeOnOverlayClick={false}
        closeOnEsc
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent maxH="90vh" borderRadius="xl">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200">
            <Flex justifyContent="space-between" alignItems="center" pr={8}>
              <Flex alignItems="center" gap={4}>
                <Heading size="md">Language Overrides</Heading>
                <FormControl w="auto" display="flex" alignItems="center">
                  <FormLabel mb={0} mr={2} fontSize="sm" fontWeight="normal">
                    Target Language:
                  </FormLabel>
                  {isLoadingLocales ? (
                    <Flex alignItems="center" gap={2}>
                      <Skeleton w="200px" h="32px" />
                      <Spinner size="xs" color="blue.500" />
                      <Text fontSize="xs" color="gray.500">
                        Loading languages...
                      </Text>
                    </Flex>
                  ) : (
                    <Flex alignItems="center" gap={2}>
                      <Select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        w="200px"
                        size="sm"
                        isDisabled={locales.length === 0 || isBusy}
                        data-testid="language-select"
                      >
                        {locales.length === 0 ? (
                          <option value="">No languages available</option>
                        ) : (
                          locales.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                              {lang.description}
                              {lang.isDefault ? ' (default)' : ''}
                            </option>
                          ))
                        )}
                      </Select>
                      {isFetchingTranslations && <Spinner size="sm" color="blue.500" />}
                      {selectedLanguage && (
                        <Tooltip label={`Delete ${getLocaleName(selectedLanguage)} translations`}>
                          <IconButton
                            aria-label="Delete language"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            isDisabled={isBusy}
                            isLoading={isDeleting}
                            onClick={handleDeleteClick}
                          />
                        </Tooltip>
                      )}
                    </Flex>
                  )}
                </FormControl>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  data-testid="csv-file-input"
                />
                {isAutoTranslateFeatureEnabled &&
                  selectedLanguage &&
                  versionId &&
                  !isSelectedTargetDefaultLocale && (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      leftIcon={<AiSparklesIcon boxSize="14px" aria-hidden />}
                      onClick={handleAutoTranslate}
                      isDisabled={isBusy}
                      isLoading={isTranslating}
                      loadingText="Translating..."
                    >
                      Translate to {getLocaleName(selectedLanguage)}
                    </Button>
                  )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  isDisabled={isBusy || !selectedLanguage}
                >
                  Import from CSV
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportTranslations}
                  isDisabled={isBusy || !selectedLanguage}
                >
                  Export to CSV
                </Button>
              </Flex>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody p={0} aria-busy={isTranslating}>
            {isLoading ? (
              <Flex h="60vh" alignItems="center" justifyContent="center" direction="column" gap={4}>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text color="gray.600" fontWeight="medium">
                  Loading translations...
                </Text>
              </Flex>
            ) : (
              <Box position="relative" h="60vh">
                <Box h="100%" overflowY="auto">
                  <Box px={6} pt={4} pb={2}>
                    <Heading size="sm" mb={2}>
                      Route Translations
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Override translations for route questions, messages, and options. Fields left
                      empty will use the default language.
                    </Text>
                  </Box>

                  <Box px={6} py={4}>
                    {groupedItems.length > 0 || routeSystemItems.length > 0 ? (
                      <VStack spacing={4} align="stretch">
                        {groupedItems.length > 0 && (
                          <Box>
                            <Heading size="xs" color="gray.600" mb={3} textTransform="uppercase">
                              Form Content
                            </Heading>
                            <VStack spacing={4} align="stretch">
                              {groupedItems.map(({ item, options }) => (
                                <Box key={item.key}>
                                  <TranslatableField
                                    item={item}
                                    overrideValue={getOverrideValue(item.key)}
                                    onChange={(value) => handleOverrideChange(item.key, value)}
                                  />
                                  {options.length > 0 && (
                                    <Box
                                      ml={6}
                                      mt={2}
                                      pl={3}
                                      borderLeft="2px solid"
                                      borderColor="gray.200"
                                    >
                                      <VStack spacing={2} align="stretch">
                                        {options.map((option) => (
                                          <TranslatableField
                                            key={option.key}
                                            item={option}
                                            overrideValue={getOverrideValue(option.key)}
                                            onChange={(value) =>
                                              handleOverrideChange(option.key, value)
                                            }
                                          />
                                        ))}
                                      </VStack>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </VStack>
                          </Box>
                        )}
                        {routeSystemItems.length > 0 && (
                          <Box>
                            <Heading size="xs" color="gray.600" mb={3} textTransform="uppercase">
                              System UI (Route Navigation)
                            </Heading>
                            <VStack spacing={2} align="stretch">
                              {routeSystemItems.map((item) => (
                                <TranslatableField
                                  key={item.key}
                                  item={item}
                                  overrideValue={getOverrideValue(item.key)}
                                  onChange={(value) => handleOverrideChange(item.key, value)}
                                />
                              ))}
                            </VStack>
                          </Box>
                        )}
                      </VStack>
                    ) : (
                      <Box
                        p={8}
                        textAlign="center"
                        bg="gray.50"
                        borderRadius="md"
                        border="1px dashed"
                        borderColor="gray.300"
                      >
                        <Text color="gray.500">No translatable fields found for this route.</Text>
                      </Box>
                    )}
                  </Box>
                </Box>
                <AutoTranslateBodyShim isActive={isTranslating} />
              </Box>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="gray.200" bg="gray.50">
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBusy}>
              Cancel
            </Button>
            <Button
              variant="action"
              onClick={handleSave}
              isLoading={isSaving}
              isDisabled={isDeleting}
              loadingText="Saving..."
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        onClose={onDeleteConfirmClose}
        onSubmit={handleDeleteConfirm}
        message={`Are you sure you want to delete the ${getLocaleName(selectedLanguage)} translations for this route?`}
        title="Delete translations"
        actionLabel="Delete"
        actionColor="red.500"
        isLoading={isDeleting}
      />
    </>
  );
}

export default RouteLanguageOverridesModal;
